/**
 * Borrador. Dos modos:
 *  - 'stroke': borra la figura completa que toca (comportamiento actual de Whiteboard)
 *  - 'area': borra solo la parte del trazo bajo el borrador (partiendo el trazo en fragmentos)
 */
import type { DrawShape, Shape } from '../../shared/types.ts'
import { distToSegment, rotatePoint } from '../lib/geom.ts'
import { deleteSubtree } from '../mindmap/actions.ts'
import { distToRelation } from '../mindmap/edges.ts'
import { hitShape } from '../canvas/hit.ts'
import { createDraw } from '../state/shapes.ts'
import { inkPoints } from '../canvas/render-shape.ts'
import type { AppContext, PointerInfo, Tool } from './types.ts'

export class EraserTool implements Tool {
  readonly id = 'eraser' as const
  readonly cursor = 'none'
  private ctx: AppContext
  private erasing = false
  private cursorPos: { x: number; y: number } | null = null
  private pending = new Set<string>()
  private removePainter: (() => void) | null = null
  private lastPos: { x: number; y: number } | null = null

  constructor(ctx: AppContext) {
    this.ctx = ctx
  }

  onActivate(): void {
    this.removePainter = this.ctx.renderer.addPainter((c) => this.paintCursor(c))
  }

  onDeactivate(): void {
    this.removePainter?.()
    this.removePainter = null
    this.cursorPos = null
    // Sin esto, las figuras que se ocultaron para dar respuesta inmediata se
    // quedaban invisibles para siempre aunque siguieran en el tablero.
    this.restoreHidden()
  }

  private get radius(): number {
    return this.ctx.store.style.eraserSize / 2
  }

  onPointerDown(p: PointerInfo): void {
    this.erasing = true
    this.pending.clear()
    this.lastPos = { x: p.x, y: p.y }
    this.eraseAt(p.x, p.y)
  }

  onPointerMove(p: PointerInfo): void {
    this.cursorPos = { x: p.x, y: p.y }
    this.ctx.renderer.invalidateOverlay()
    if (!this.erasing) return
    // Interpola entre eventos para no dejar huecos al mover rápido.
    const last = this.lastPos
    if (last) {
      const d = Math.hypot(p.x - last.x, p.y - last.y)
      const steps = Math.min(24, Math.ceil(d / (this.radius || 8)))
      for (let i = 1; i <= steps; i++) {
        this.eraseAt(last.x + ((p.x - last.x) * i) / steps, last.y + ((p.y - last.y) * i) / steps)
      }
    }
    this.eraseAt(p.x, p.y)
    this.lastPos = { x: p.x, y: p.y }
  }

  onPointerUp(): void {
    if (!this.erasing) return
    this.erasing = false
    this.lastPos = null
    if (this.pending.size > 0) {
      const ids = Array.from(this.pending)
      // Un tema del mapa se borra con TODO su subárbol y sus relaciones, y el
      // mapa se reacomoda — igual que Supr. Antes quedaban huérfanos rancios.
      const temas = ids.filter((id) => this.ctx.board.get(id)?.type === 'topic')
      const resto = ids.filter((id) => this.ctx.board.get(id)?.type !== 'topic')
      for (const id of temas) deleteSubtree(this.ctx.board, id)
      if (resto.length > 0) this.ctx.board.delete(resto)
      for (const id of ids) this.ctx.renderer.hidden.delete(id)
      this.pending.clear()
      this.ctx.touch()
    }
  }

  onCancel(): void {
    this.erasing = false
    this.lastPos = null
    this.restoreHidden()
  }

  /** Vuelve a mostrar lo que se ocultó pero no llegó a borrarse. */
  private restoreHidden(): void {
    if (this.pending.size === 0) return
    for (const id of this.pending) this.ctx.renderer.hidden.delete(id)
    this.pending.clear()
    this.ctx.renderer.invalidate()
  }

  private eraseAt(x: number, y: number): void {
    const mode = this.ctx.store.style.eraserMode
    const shapes = this.ctx.shapes()
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i]
      if (shape.locked || this.pending.has(shape.id)) continue
      // Lo que está escondido en una rama plegada no se puede borrar sin verlo.
      if ((shape.type === 'topic' || shape.type === 'relation') && shape.hidden) continue
      // Las relaciones se tocan por su curva, no por su caja (que es enorme).
      if (shape.type === 'relation') {
        const a = this.ctx.board.get(shape.from)
        const b = this.ctx.board.get(shape.to)
        const cerca =
          a?.type === 'topic' && b?.type === 'topic' && distToRelation(x, y, a, b) <= this.radius + 4
        if (!cerca) continue
        this.pending.add(shape.id)
        this.ctx.renderer.hidden.add(shape.id)
        this.ctx.renderer.invalidate()
        continue
      }
      if (!hitShape(shape, x, y, this.radius)) continue
      if (mode === 'area' && shape.type === 'draw') {
        this.splitStroke(shape, x, y)
      } else {
        this.pending.add(shape.id)
        // Ocultar de inmediato da sensación de respuesta instantánea.
        this.ctx.renderer.hidden.add(shape.id)
        this.ctx.renderer.invalidate()
      }
    }
  }

  /** Corta un trazo en los tramos que no toca el borrador. */
  private splitStroke(shape: DrawShape, x: number, y: number): void {
    const pts = inkPoints(shape)
    // El punto del borrador se lleva al espacio propio del trazo deshaciendo su
    // giro; si no, sobre un trazo rotado se borraba en el sitio equivocado.
    const center = { x: shape.x + shape.w / 2, y: shape.y + shape.h / 2 }
    const unrotated = shape.rotation
      ? rotatePoint(x, y, center.x, center.y, -shape.rotation)
      : { x, y }
    const local = { x: unrotated.x - shape.x, y: unrotated.y - shape.y }
    const r = this.radius + shape.size / 2
    const segments: { x: number; y: number; p: number }[][] = []
    let current: { x: number; y: number; p: number }[] = []
    for (const pt of pts) {
      const inside = Math.hypot(pt.x - local.x, pt.y - local.y) <= r
      if (inside) {
        if (current.length > 1) segments.push(current)
        current = []
      } else {
        current.push(pt)
      }
    }
    if (current.length > 1) segments.push(current)

    const replacements: Shape[] = []
    let z = shape.z
    for (const seg of segments) {
      if (seg.length < 2) continue
      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity
      for (const pt of seg) {
        minX = Math.min(minX, pt.x)
        maxX = Math.max(maxX, pt.x)
        minY = Math.min(minY, pt.y)
        maxY = Math.max(maxY, pt.y)
      }
      const flat: number[] = []
      for (const pt of seg) flat.push(pt.x - minX, pt.y - minY, pt.p)
      const fragment = createDraw({
        points: flat,
        x: shape.x + minX,
        y: shape.y + minY,
        w: Math.max(maxX - minX, 0.01),
        h: Math.max(maxY - minY, 0.01),
        color: shape.color,
        size: shape.size,
        highlighter: shape.highlighter,
        createdBy: shape.createdBy,
        z: z++,
      })
      // Los trozos heredan el giro, la sensibilidad y la escala del original:
      // sin esto el trazo saltaba de sitio y cambiaba de grosor al borrar.
      fragment.rotation = shape.rotation
      fragment.thinning = shape.thinning
      fragment.baseW = shape.baseW
      fragment.baseH = shape.baseH
      replacements.push(fragment)
    }
    this.ctx.board.transact(() => {
      this.ctx.board.shapes.delete(shape.id)
      for (const r2 of replacements) this.ctx.board.shapes.set(r2.id, r2)
    })
    this.ctx.renderer.invalidate()
  }

  private paintCursor(c: CanvasRenderingContext2D): void {
    if (!this.cursorPos) return
    const inv = 1 / this.ctx.store.camera.z
    c.save()
    c.beginPath()
    c.arc(this.cursorPos.x, this.cursorPos.y, this.radius, 0, Math.PI * 2)
    c.strokeStyle = '#5A5A5A'
    c.lineWidth = 1.5 * inv
    c.fillStyle = 'rgba(255,255,255,0.55)'
    c.fill()
    c.stroke()
    c.restore()
  }

  /** Al borrar por figura completa se ocultan primero; hay que limpiarlo al cambiar de herramienta. */
  clearHidden(): void {
    this.ctx.renderer.hidden.clear()
  }
}
